import type {
  CreateProjectDto,
  ProjectDto,
  ProjectTaskSummaryDto,
  UpdateProjectDto,
} from '@schwrz/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '../shared/api/client';
import { projectApiParam } from '../shared/lib/project-route';

export const useProjectsStore = defineStore('projects', () => {
  const items = ref<ProjectDto[]>([]);
  const loaded = ref(false);
  const loading = ref(false);
  const selectedId = ref<string | null>(null);
  /** Legacy openProjectActionMenuId — one project "…" menu at a time. */
  const openActionMenuProjectId = ref<string | null>(null);
  /** Project card "…" menu on company details page. */
  const openDetailsProjectMenuId = ref<string | null>(null);
  /** Project card "…" menu on company projects section page. */
  const openSectionProjectMenuId = ref<string | null>(null);
  const taskSummaries = ref(new Map<string, ProjectTaskSummaryDto>());

  function ofClient(clientId: string): ProjectDto[] {
    return items.value
      .filter((p) => p.clientId === clientId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /** Lookup by route/query param — numeric public id or legacy UUID. */
  function getByRef(ref: string): ProjectDto | undefined {
    if (/^\d+$/.test(ref)) {
      const n = Number(ref);
      return items.value.find((p) => p.number === n);
    }
    return items.value.find((p) => p.id === ref);
  }

  function apiParamFor(ref: string): string {
    const p = getByRef(ref);
    return p ? projectApiParam(p) : ref;
  }

  const selected = computed<ProjectDto | null>(
    () => items.value.find((p) => p.id === selectedId.value) ?? null,
  );

  async function load(force = false): Promise<void> {
    if (loaded.value && !force) return;
    loading.value = true;
    try {
      const res = await api<{ projects: ProjectDto[] }>('/api/projects?includeArchived=true');
      items.value = res.projects;
      loaded.value = true;
    } finally {
      loading.value = false;
    }
  }

  async function loadTaskSummary(): Promise<void> {
    const res = await api<{ summaries: ProjectTaskSummaryDto[] }>('/api/projects/task-summary');
    const map = new Map<string, ProjectTaskSummaryDto>();
    for (const row of res.summaries) map.set(row.projectId, row);
    taskSummaries.value = map;
  }

  function taskSummaryOf(projectId: string): ProjectTaskSummaryDto | undefined {
    return taskSummaries.value.get(projectId);
  }

  async function create(payload: CreateProjectDto): Promise<ProjectDto> {
    const created = await api<ProjectDto>('/api/projects', { method: 'POST', body: payload });
    items.value = [...items.value, created];
    return created;
  }

  async function update(id: string, payload: UpdateProjectDto): Promise<ProjectDto> {
    const updated = await api<ProjectDto>(`/api/projects/${apiParamFor(id)}`, {
      method: 'PATCH',
      body: payload,
    });
    items.value = items.value.map((p) => (p.id === updated.id ? updated : p));
    return updated;
  }

  async function remove(id: string): Promise<void> {
    const internalId = getByRef(id)?.id ?? id;
    await api<void>(`/api/projects/${apiParamFor(id)}`, { method: 'DELETE' });
    items.value = items.value.filter((p) => p.id !== internalId);
    if (selectedId.value === internalId) selectedId.value = null;
  }

  function select(id: string | null): void {
    selectedId.value = id;
  }

  function setActionMenuOpen(id: string | null): void {
    openActionMenuProjectId.value = id;
  }

  function setDetailsMenuOpen(id: string | null): void {
    openDetailsProjectMenuId.value = id;
  }

  function setSectionMenuOpen(id: string | null): void {
    openSectionProjectMenuId.value = id;
  }

  return {
    items,
    selected,
    selectedId,
    openActionMenuProjectId,
    openDetailsProjectMenuId,
    openSectionProjectMenuId,
    loaded,
    loading,
    taskSummaries,
    loadTaskSummary,
    taskSummaryOf,
    ofClient,
    getByRef,
    load,
    create,
    update,
    remove,
    select,
    setActionMenuOpen,
    setDetailsMenuOpen,
    setSectionMenuOpen,
  };
});
